import React, { useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { createRoot } from 'react-dom/client';
import {
  UI,
  THEMES,
  circleLabel,
  communityLabel,
  detectLanguage,
  detectTheme,
  difficultyLabel,
  resourceCategoryLabel,
  serviceTypeLabel,
  siteLanguageLabel,
  siteAccessNote,
  siteDescription,
  statusLabel,
  termLabel,
} from './i18n.js';
import {
  ANNIVERSARY_WINDOW_DAYS,
  formatCalendarDate,
  nextAnniversary,
  siteLifecycleState,
  upcomingAnniversaries,
} from './anniversary.js';
import { decodeIndex, searchIndex, sortDirectory } from './search-core.js';
import { applySavePtSnapshot, formatMonitorTime, isSavePtSnapshot } from './savept-monitor.js';
import { FeedbackAdminPage, FeedbackDialog, feedbackAdminPath, feedbackLabel } from './feedback.jsx';
import './styles.css';

const PAGE_SIZE = 24;
const QUICK_SEARCH_PLAN = [
  ['sites', 1],
  ['content', 2],
  ['quality', 2],
  ['community', 1],
];

function shuffle(items) {
  const result = [...items];
  for (let index = result.length - 1; index > 0; index -= 1) {
    const target = Math.floor(Math.random() * (index + 1));
    [result[index], result[target]] = [result[target], result[index]];
  }
  return result;
}

function prepareQuickSearches(groups) {
  return Object.fromEntries(Object.entries(groups).map(([group, items]) => [group, shuffle(items)]));
}

function pickQuickSearches(decks, round) {
  const selected = QUICK_SEARCH_PLAN.flatMap(([group, count]) => {
    const deck = decks[group] ?? [];
    return Array.from(
      { length: Math.min(count, deck.length) },
      (_, index) => deck[(round * count + index) % deck.length],
    );
  });
  return shuffle([...new Set(selected)]);
}

function SiteMark({ site, large = false }) {
  const [failed, setFailed] = useState(false);
  const initials = site.name.replace(/[^A-Za-z0-9\u3400-\u9fff]/g, '').slice(0, 2).toUpperCase();
  return (
    <span className={`site-mark${large ? ' site-mark--large' : ''}`} aria-hidden="true">
      <span className="site-mark__initials">{initials}</span>
      {!failed && site.logo && <img src={site.logo} alt="" loading="lazy" onError={() => setFailed(true)} />}
    </span>
  );
}

function StatusPill({ status, language }) {
  const tone = status.includes('跑路') ? 'danger' : status.includes('开放') ? 'open' : 'neutral';
  return <span className={`status status--${tone}`}>{statusLabel(status, language)}</span>;
}

function LifecyclePill({ state, copy }) {
  const label = { alive: copy.siteAlive, down: copy.siteDown, dead: copy.siteDead, unknown: copy.siteUnknown }[state];
  return (
    <span className={`lifecycle-pill lifecycle-pill--${state}`} aria-label={`${copy.siteStatus}: ${label}`}>
      {label}
    </span>
  );
}

function SiteCard({ result, onOpen, onFilterLabel, language, copy, semanticReady }) {
  const { site, semantic, lexical } = result;
  const score = Math.max(0, Math.min(99, Math.round((semantic * 0.74 + lexical * 0.26) * 100)));
  const content = site.content_types.slice(0, 3);
  const specialties = site.specialties.slice(0, Math.max(0, 4 - content.length));
  const foundedAt = site.community_profile?.founded_at;
  const closedAt = site.community_profile?.closed_at;
  const lifecycle = siteLifecycleState(site);
  const closed = lifecycle === 'dead';
  return (
    <article className={`site-card site-card--${lifecycle}`}>
      <a
        className="site-card__visit"
        href={site.url}
        target="_blank"
        rel="noreferrer"
        aria-label={`${copy.visit}: ${site.name}`}
        title={copy.visit}
      >
        <span aria-hidden="true">↗</span>
      </a>
      <div className="site-card__body">
        <button className="site-card__open" type="button" onClick={() => onOpen(site)} aria-label={copy.openDetails(site.name)} />
        <div className="site-card__head">
          <SiteMark site={site} />
          <div className="site-card__title">
            <h2>{site.name}</h2>
            <p>{site.alias || site.source_name}</p>
          </div>
          <LifecyclePill state={lifecycle} copy={copy} />
        </div>
        <div className="community-row">
          <span className="scope-pill">{circleLabel(site.pt_circle, language)}</span>
          {site.community_labels.slice(0, 2).map((item) => <button className="community-tag" type="button" key={item} onClick={() => onFilterLabel(item)} aria-label={copy.filterByLabel(communityLabel(item, language))}>{communityLabel(item, language)}</button>)}
          {foundedAt && <span className="founded-tag">{copy.foundedShort(formatCalendarDate(foundedAt, language))}</span>}
        </div>
        <p className="site-card__description">{siteDescription(site, language)}</p>
        <div className="chip-row">
          {content.map((item) => <span className="chip" key={item}>{termLabel(item, language)}</span>)}
          {specialties.map((item) => <span className="chip chip--accent" key={item}>{termLabel(item, language)}</span>)}
        </div>
      </div>
      <footer className="site-card__footer">
        {closed
          ? <span className="closed-summary">{closedAt ? copy.closedShort(formatCalendarDate(closedAt, language)) : copy.closedDateUnknown}</span>
          : <StatusPill status={site.registration} language={language} />}
        {(semantic || lexical) > 0 && (
          <span className="match">{result.matchKind === 'name' ? copy.nameMatch : semanticReady ? copy.match(score) : copy.textMatch}</span>
        )}
      </footer>
    </article>
  );
}

function AnniversaryPanel({ entries, onOpen, language, copy }) {
  if (!entries.length) return null;
  return (
    <section className="anniversaries" aria-labelledby="anniversary-title">
      <div className="anniversaries__header">
        <div>
          <p className="eyebrow">{copy.anniversaryEyebrow}</p>
          <h2 id="anniversary-title">{copy.anniversaryTitle}</h2>
        </div>
        <p>{copy.anniversaryWindow(ANNIVERSARY_WINDOW_DAYS, entries.length)}</p>
      </div>
      <div className="anniversaries__list">
        {entries.map((entry) => (
          <button className="anniversary-card" type="button" key={entry.site.id} onClick={() => onOpen(entry.site)}>
            <SiteMark site={entry.site} />
            <span className="anniversary-card__site">
              <strong>{entry.site.name}</strong>
              <small>{formatCalendarDate(entry.site.community_profile.founded_at, language, { includeYear: false })}</small>
            </span>
            <span className={`anniversary-card__countdown${entry.daysUntil === 0 ? ' anniversary-card__countdown--today' : ''}`}>
              {copy.anniversaryCountdown(entry.daysUntil, entry.years)}
            </span>
          </button>
        ))}
      </div>
    </section>
  );
}

function SiteDialog({ site, onClose, onFilterLabel, language, copy }) {
  const ref = useRef(null);
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog || !site) return undefined;
    if (!dialog.open) dialog.showModal();
    return () => { if (dialog.open) dialog.close(); };
  }, [site]);
  if (!site) return null;
  const foundedAt = site.community_profile?.founded_at;
  const closedAt = site.community_profile?.closed_at;
  const lifecycle = siteLifecycleState(site);
  const closed = lifecycle === 'dead';
  const anniversary = nextAnniversary(site);

  return (
    <dialog ref={ref} className="detail" onClose={onClose} onClick={(event) => { if (event.target === ref.current) ref.current.close(); }}>
      <div className="detail__panel">
        <button className="detail__close" type="button" onClick={() => ref.current.close()} aria-label={copy.close}>×</button>
        <header className="detail__header">
          <SiteMark site={site} large />
          <div>
            <p className="eyebrow">{serviceTypeLabel(site.service_type, language)} · {circleLabel(site.pt_circle, language)}</p>
            <h2>{site.name}</h2>
            {site.alias && <p className="detail__alias">{site.alias}</p>}
          </div>
        </header>
        <div className="detail__status">
          <LifecyclePill state={lifecycle} copy={copy} />
          {!closed && <StatusPill status={site.registration} language={language} />}
          <span>{difficultyLabel(site, language)}</span>
        </div>
        <section>
          <h3>{copy.introduction}</h3>
          <p>{siteDescription(site, language)}</p>
        </section>
        <section>
          <h3>{copy.resources}</h3>
          <div className="chip-row chip-row--roomy">
            {[...site.content_types, ...site.specialties].map((item) => <span className="chip" key={item}>{termLabel(item, language)}</span>)}
          </div>
        </section>
        {site.community_labels.length > 0 && (
          <section>
            <h3>{copy.communityLabels}</h3>
            <div className="community-row community-row--detail">
              {site.community_labels.map((item) => <button className="community-tag" type="button" key={item} onClick={() => onFilterLabel(item)} aria-label={copy.filterByLabel(communityLabel(item, language))}>{communityLabel(item, language)}</button>)}
            </div>
          </section>
        )}
        {site.official_groups.length > 0 && (
          <section>
            <h3>{copy.releaseGroups}</h3>
            <p>{site.official_groups.join(' · ')}</p>
          </section>
        )}
        <section>
          <h3>{copy.tips}</h3>
          <p>{siteAccessNote(site, language)}</p>
        </section>
        <dl className="facts">
          {foundedAt && <div><dt>{copy.foundedAt}</dt><dd>{formatCalendarDate(foundedAt, language)}</dd></div>}
          {closedAt && <div><dt>{copy.closedAt}</dt><dd>{formatCalendarDate(closedAt, language)}</dd></div>}
          {anniversary && <div><dt>{copy.nextAnniversary}</dt><dd>{copy.anniversaryCountdown(anniversary.daysUntil, anniversary.years)}</dd></div>}
          {site.monitor && <div><dt>{copy.monitorSource}</dt><dd>{copy.monitorObserved(formatMonitorTime(site.monitor.observedAt, language))}</dd></div>}
          <div><dt>{copy.language}</dt><dd>{siteLanguageLabel(site.language, language)}</dd></div>
          <div><dt>{copy.serviceType}</dt><dd>{serviceTypeLabel(site.service_type, language)}</dd></div>
          <div><dt>{copy.sourceCount}</dt><dd>{site.sources.length}</dd></div>
          <div><dt>{copy.checkedAt}</dt><dd>{site.checked_at}</dd></div>
        </dl>
        <div className="detail__actions">
          <a className="primary-link" href={site.url} target="_blank" rel="noreferrer">{copy.visit} <span>↗</span></a>
          <div className="source-links">
            {site.sources.map((source, index) => <a key={source.url} href={source.url} target="_blank" rel="noreferrer">{copy.sourceLink(index + 1)}</a>)}
          </div>
        </div>
      </div>
    </dialog>
  );
}

function App() {
  const [language, setLanguage] = useState(detectLanguage);
  const [theme, setTheme] = useState(detectTheme);
  const [index, setIndex] = useState(null);
  const [monitorSnapshot, setMonitorSnapshot] = useState(null);
  const [indexError, setIndexError] = useState('');
  const [model, setModel] = useState({ state: 'loading', embed: null });
  const [query, setQuery] = useState('');
  const [circle, setCircle] = useState('');
  const [label, setLabel] = useState('');
  const labelFilterRef = useRef(null);
  const [resourceCategory, setResourceCategory] = useState('');
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [selectedId, setSelectedId] = useState(null);
  const [quickSearchRound, setQuickSearchRound] = useState(0);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [visitCounts, setVisitCounts] = useState(null);
  const deferredQuery = useDeferredValue(query);
  const copy = UI[language];

  useEffect(() => {
    document.documentElement.lang = copy.htmlLang;
    document.title = copy.title;
    try { localStorage.setItem('pt-sites-language', language); } catch {}
  }, [copy, language]);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    const selectedTheme = THEMES.find((item) => item.id === theme);
    const themeColor = document.querySelector('meta[name="theme-color"]');
    if (selectedTheme && themeColor) themeColor.content = selectedTheme.background;
    try { localStorage.setItem('pt-sites-theme', theme); } catch {}
  }, [theme]);

  useEffect(() => {
    const controller = new AbortController();
    fetch(`${import.meta.env.BASE_URL}data/site-index.bin`, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error(`Index request failed (${response.status})`);
        return response.arrayBuffer();
      })
      .then((buffer) => setIndex(decodeIndex(buffer)))
      .catch((reason) => { if (reason.name !== 'AbortError') setIndexError(reason.message || 'Index initialization failed'); });
    return () => controller.abort();
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    fetch(`${import.meta.env.BASE_URL}api/visits`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Visit counter request failed (${response.status})`);
        return response.json();
      })
      .then((counts) => {
        if (Number.isSafeInteger(counts.total) && Number.isSafeInteger(counts.today)) setVisitCounts(counts);
      })
      .catch((reason) => {
        if (reason.name !== 'AbortError') console.info('Visit counter is unavailable');
      });
    return () => controller.abort();
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    fetch(`${import.meta.env.BASE_URL}api/savept-status`, {
      headers: { Accept: 'application/json' },
      signal: controller.signal,
    })
      .then((response) => {
        if (!response.ok) throw new Error(`SavePT monitor request failed (${response.status})`);
        return response.json();
      })
      .then((snapshot) => {
        if (isSavePtSnapshot(snapshot)) setMonitorSnapshot(snapshot);
      })
      .catch((reason) => {
        if (reason.name !== 'AbortError') console.info('SavePT monitor is unavailable; using bundled site data');
      });
    return () => controller.abort();
  }, []);

  useEffect(() => {
    let active = true;
    const load = () => {
      import('@ternlight/mini')
        .then((module) => {
          module.embed('local semantic search warmup');
          if (active) setModel({ state: 'ready', embed: module.embed });
        })
        .catch((reason) => {
          console.error('Ternlight failed to load', reason);
          if (active) setModel({ state: 'error', embed: null });
        });
    };
    const handle = 'requestIdleCallback' in window
      ? window.requestIdleCallback(load, { timeout: 300 })
      : window.setTimeout(load, 0);
    return () => {
      active = false;
      if ('cancelIdleCallback' in window) window.cancelIdleCallback(handle);
      else window.clearTimeout(handle);
    };
  }, []);

  useEffect(() => setVisible(PAGE_SIZE), [deferredQuery, circle, resourceCategory, label]);

  const monitored = useMemo(() => applySavePtSnapshot(index, monitorSnapshot), [index, monitorSnapshot]);
  const directoryIndex = monitored.index;
  const selectedSite = useMemo(() => directoryIndex?.sites.find((site) => site.id === selectedId) ?? null, [directoryIndex, selectedId]);
  const openSite = (site) => setSelectedId(site.id);
  const filterByLabel = (item) => {
    setLabel(item);
    setSelectedId(null);
    requestAnimationFrame(() => {
      labelFilterRef.current?.focus({ preventScroll: true });
      labelFilterRef.current?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    });
  };
  const communityLabels = useMemo(() => directoryIndex ? [...new Set(directoryIndex.sites.flatMap((site) => site.community_labels))].sort() : [], [directoryIndex]);
  const circles = useMemo(() => directoryIndex ? ['inner', 'outer', 'other'].filter((value) => directoryIndex.sites.some((site) => (site.pt_circle ?? 'other') === value)) : [], [directoryIndex]);
  const resourceCategories = useMemo(() => {
    if (!directoryIndex) return [];
    const order = ['综合', '影视', '动漫', '音乐', '图书', '学习', '体育', '游戏', '软件', '少儿', '成人', '论坛资讯', '其他'];
    const available = new Set(directoryIndex.sites.flatMap((site) => site.resource_categories));
    return order.filter((item) => available.has(item));
  }, [directoryIndex]);
  const quickSearchDecks = useMemo(() => prepareQuickSearches(copy.recommendationGroups), [copy.recommendationGroups]);
  const quickSearches = useMemo(() => pickQuickSearches(quickSearchDecks, quickSearchRound), [quickSearchDecks, quickSearchRound]);
  const anniversaries = useMemo(() => directoryIndex ? upcomingAnniversaries(directoryIndex.sites) : [], [directoryIndex]);
  const results = useMemo(() => {
    if (!directoryIndex) return [];
    let ranked = deferredQuery.trim()
      ? searchIndex(directoryIndex, deferredQuery, model.embed)
      : sortDirectory(searchIndex(directoryIndex, '', null));
    if (deferredQuery.trim() && !model.embed) ranked = ranked.filter((result) => result.lexical > 0);
    return ranked.filter(({ site }) => {
      const siteCircle = site.pt_circle ?? 'other';
      return (!label || site.community_labels.includes(label)) && (!circle || siteCircle === circle) && (!resourceCategory || site.resource_categories.includes(resourceCategory));
    });
  }, [directoryIndex, deferredQuery, circle, resourceCategory, label, model.embed]);

  const setUiLanguage = (nextLanguage) => {
    setLanguage(nextLanguage);
    try { localStorage.setItem('pt-sites-language', nextLanguage); } catch {}
  };

  const setUiTheme = (nextTheme) => {
    setTheme(nextTheme);
    try { localStorage.setItem('pt-sites-theme', nextTheme); } catch {}
  };

  const visitNumber = useMemo(
    () => new Intl.NumberFormat(language === 'en' ? 'en-US' : 'zh-CN'),
    [language],
  );

  return (
    <div className="app-shell">
      <header className="topbar">
        <a className="brand" href="/" aria-label="PT Sites">
          <img className="brand__icon" src="/favicon.svg" alt="" width="38" height="38" />
          <span>PT SITES</span>
        </a>
        <div className="topbar__actions">
          <p className="topbar__meta">{index ? copy.headerStatus(index.count) : copy.loadingDirectory}</p>
          <div className="theme-switch" role="group" aria-label={copy.themeLabel}>
            {THEMES.map((item) => (
              <button
                type="button"
                key={item.id}
                aria-label={copy.themeNames[item.id]}
                aria-pressed={theme === item.id}
                title={copy.themeNames[item.id]}
                style={{ '--swatch': item.color }}
                onClick={() => setUiTheme(item.id)}
              />
            ))}
          </div>
          <div className="language-switch" role="group" aria-label="Language / 语言">
            <button type="button" aria-pressed={language === 'zh'} onClick={() => setUiLanguage('zh')}>中文</button>
            <button type="button" aria-pressed={language === 'en'} onClick={() => setUiLanguage('en')}>EN</button>
          </div>
        </div>
      </header>

      <main>
        <section className="search-stage" aria-labelledby="page-title">
          <div className="search-stage__headline">
            <div className="search-stage__copy">
              <h1 id="page-title">{copy.heroTitle}</h1>
              <p>{copy.heroBody}</p>
            </div>
            {visitCounts && (
              <dl className="visit-stats" aria-label={copy.visitStats}>
                <div><dt>{copy.totalVisits}</dt><dd>{visitNumber.format(visitCounts.total)}</dd></div>
                <div><dt>{copy.todayVisits}</dt><dd>{visitNumber.format(visitCounts.today)}</dd></div>
              </dl>
            )}
          </div>
          <label className={`search-box${index ? ' search-box--ready' : ''}`}>
            <span aria-hidden="true">⌕</span>
            <input
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={index ? copy.searchPlaceholder : copy.indexLoadingPlaceholder}
              disabled={!index}
              autoComplete="off"
              aria-label={copy.searchLabel}
            />
            {query && <button type="button" onClick={() => setQuery('')} aria-label={copy.clearSearch}>×</button>}
          </label>
          <div className="quick-searches">
            <div className="quick-searches__header">
              <span>{copy.recommendationsLabel}</span>
              <button
                className="quick-searches__shuffle"
                type="button"
                onClick={() => setQuickSearchRound((value) => value + 1)}
                aria-label={copy.shuffleRecommendationsLabel}
              >
                <span aria-hidden="true">↻</span> {copy.shuffleRecommendations}
              </button>
            </div>
            <div className="quick-searches__list" aria-label={copy.recommendationsLabel} aria-live="polite">
              {quickSearches.map((item) => <button type="button" key={item} onClick={() => setQuery(item)} disabled={!index}>{item}</button>)}
            </div>
          </div>
          <div className="engine-note">
            <span className={`pulse${model.state === 'ready' ? ' pulse--ready' : ''}${model.state === 'error' ? ' pulse--error' : ''}`} />
            {indexError ? <strong>{copy.indexErrorTitle}</strong> : !index ? <span>{copy.indexLoading}</span> : model.state === 'ready' ? <><strong>{copy.semanticReady}</strong><span>{copy.semanticDetails()}</span></> : model.state === 'error' ? <strong>{copy.semanticFailed}</strong> : <><strong>{copy.textReady}</strong><span>{copy.semanticLoading}</span></>}
          </div>
        </section>

        {directoryIndex && <AnniversaryPanel entries={anniversaries} onOpen={openSite} language={language} copy={copy} />}

        <section className="directory" aria-live="polite" aria-busy={!index}>
          <div className="directory__toolbar">
            <div>
              <p className="eyebrow">{copy.directory}</p>
              <h2>{deferredQuery ? copy.resultTitle(deferredQuery) : copy.directoryTitle}</h2>
            </div>
            <div className="filters">
              <label>{copy.communityLabels}<select ref={labelFilterRef} value={label} onChange={(event) => setLabel(event.target.value)}><option value="">{copy.all}</option>{communityLabels.map((item) => <option value={item} key={item}>{communityLabel(item, language)}</option>)}</select></label>
              <label>{copy.circle}<select value={circle} onChange={(event) => setCircle(event.target.value)}><option value="">{copy.all}</option>{circles.map((item) => <option value={item} key={item}>{circleLabel(item, language)}</option>)}</select></label>
              <label>{copy.resourceDirection}<select value={resourceCategory} onChange={(event) => setResourceCategory(event.target.value)}><option value="">{copy.all}</option>{resourceCategories.map((item) => <option value={item} key={item}>{resourceCategoryLabel(item, language)}</option>)}</select></label>
            </div>
          </div>
          <p className="filter-note">{copy.circleNote}</p>
          {directoryIndex && <p className="result-count">{copy.resultCount(results.length)}</p>}
          {indexError && <div className="empty-state"><h3>{copy.indexErrorTitle}</h3><p>{copy.indexErrorBody}</p></div>}
          {!indexError && directoryIndex && results.length === 0 && <div className="empty-state"><h3>{copy.emptyTitle}</h3><p>{copy.emptyBody}</p></div>}
          <div className="site-grid">
            {results.slice(0, visible).map((result) => <SiteCard result={result} onOpen={openSite} onFilterLabel={filterByLabel} language={language} copy={copy} semanticReady={model.state === 'ready'} key={result.site.id} />)}
          </div>
          {visible < results.length && <button className="load-more" type="button" onClick={() => setVisible((value) => value + PAGE_SIZE)}>{copy.loadMore(results.length - visible)}</button>}
        </section>
      </main>

      <footer className="page-footer">
        <span>PT Sites</span>
        <p>{copy.footer}</p>
      </footer>
      <button className="feedback-launch" type="button" onClick={() => setFeedbackOpen(true)} aria-label={feedbackLabel(language)}>
        <span aria-hidden="true">✎</span><span className="feedback-launch__label">{feedbackLabel(language)}</span>
      </button>
      <SiteDialog onFilterLabel={filterByLabel} site={selectedSite} onClose={() => setSelectedId(null)} language={language} copy={copy} />
      <FeedbackDialog open={feedbackOpen} onClose={() => setFeedbackOpen(false)} language={language} sites={directoryIndex?.sites ?? []} />
    </div>
  );
}

const container = document.getElementById('root');
const root = container.__ptSitesRoot ?? createRoot(container);
container.__ptSitesRoot = root;
root.render(feedbackAdminPath(window.location.pathname) ? <FeedbackAdminPage /> : <App />);
